import {
    getLocation,
    push,
    LOCATION_CHANGE,
    replace,
} from 'connected-react-router';
import { stringify, parse } from 'query-string';
import companyStatsService from '../../services/companyStatsService';
import orderMetaService from '../../services/orderMetaService';
import { setLoading, setErrorMessage } from './ui';
import { logout } from './user';
import { getDefaultFrom, getDefaultTo, getYesterday } from '../storeUtils/dateUtils';
import routeConstants from '../../Routes/routeConstants';
import { isUnauthorized } from '../../services/utils/responseValidator';
import translation from '../../translation/translation';
import formatGraphData from '../storeUtils/companyReportDataUtils';
import { mapCompanySummaryToCompanyTable, mapCompanySummaryToSelectTable } from '../../components/CompanyReport/companyReportTableAdapter';

// ACTIONS
const SET_COMPANY_REPORT_DATA = 'SET_COMPANY_REPORT_DATA';
const CLEAR_COMPANY_REPORT_DATA = 'CLEAR_COMPANY_REPORT_DATA';
const CLEAR_COMPANY_REPORT_FORM = 'CLEAR_COMPANY_REPORT_FORM';
const SET_COMPANY_REPORT_FORM_STATE = 'SET_COMPANY_REPORT_FORM_STATE';
const SET_COMPANY_REPORT_FORM_FIELD = 'SET_COMPANY_REPORT_FORM_FIELD';
const SET_COMPANY_REPORT_LOCATION = 'SET_COMPANY_REPORT_LOCATION';
const SET_COMPANY_REPORT_HEADER = 'SET_COMPANY_REPORT_HEADER';
const SET_SHOW_GRAPHS = 'SET_SHOW_GRAPHS';
const SET_SHOW_CUSTOMER_CONTACTS = 'SET_SHOW_CUSTOMER_CONTACTS';
const SET_SHOW_IMPRESSIONS = 'SET_SHOW_IMPRESSIONS';
const SET_SHOW_PER_HID = 'SET_SHOW_PER_HID';
const SET_SEARCH_ACTIVE = 'SET_SEARCH_ACTIVE';
const SET_USE_ORDER_PERIOD = 'SET_USE_ORDER_PERIOD';
const SET_RESULT_PERIOD = 'SET_RESULT_PERIOD';
const SET_RESULT_SEARCH_TYPE = 'SET_RESULT_SEARCH_TYPE';
const SET_NOT_GROUPED_PRODUCTS = 'SET_NOT_GROUPED_PRODUCTS';

const initialState = {
    formState: {
        searchType: 'COMPANY_NAME',
        HID: '',
        ORG_NUMBER: '',
        COMPANY_NAME: '',
        ORDER_NUMBER: '',
        from: getDefaultFrom(),
        to: getDefaultTo(),
    },
    location: {
        pathname: routeConstants.COMPANY_REPORT,
        hash: '',
        search: '',
    },
    reportGraphData: null,
    searchActive: false,
    resultPeriodFrom: null,
    resultPeriodTo: null,
    resultSearchType: null,
    showGraphs: true,
    showCustomerContacts: true,
    showImpressions: true,
    showPerHid: false,
    useOrderPeriod: true,
    notGroupedProducts: [
        'telefonnr',
        'hemsidelank',
        'route',
        'show_on_map',
    ],
};

// REDUCER
const reducer = (state = initialState, action) => {
    switch (action.type) {
    case SET_COMPANY_REPORT_DATA: {
        const {
            totalLeadCount,
            totalImpressionCount,
            totalDetailImpressionCount,
            totalListImpressionCount,
            companySummary,
            productSummary,
            reportGraphData,
            companyTableData,
            selectCompanyTableData,
        } = action.value;

        const companyCount = companySummary ? companySummary.length : null;

        return Object.assign({}, state, {
            totalLeadCount,
            totalImpressionCount,
            totalDetailImpressionCount,
            totalListImpressionCount,
            productSummary,
            reportGraphData,
            companyCount,
            companyTableData,
            selectCompanyTableData,
        });
    }
    case CLEAR_COMPANY_REPORT_DATA: {
        return Object.assign({}, state, {
            searchActive: false,
            reportGraphData: null,
            totalLeadCount: null,
            totalImpressionCount: null,
            totalDetailImpressionCount: null,
            totalListImpressionCount: null,
            productSummary: null,
            companyCount: null,
            companyTableData: null,
            selectCompanyTableData: null,
        });
    }
    case CLEAR_COMPANY_REPORT_FORM: {
        return Object.assign({}, state, {
            formState: initialState.formState,
        });
    }
    case SET_COMPANY_REPORT_FORM_STATE: {
        return Object.assign({}, state, {
            formState: action.value,
        });
    }
    case SET_COMPANY_REPORT_FORM_FIELD: {
        return Object.assign({}, state, {
            formState: { ...state.formState, ...action.value },
        });
    }
    case SET_COMPANY_REPORT_LOCATION: {
        return Object.assign({}, state, {
            location: action.value,
        });
    }
    case SET_COMPANY_REPORT_HEADER: {
        return Object.assign({}, state, {
            reportHeader: action.value,
        });
    }
    case SET_SHOW_GRAPHS: {
        return Object.assign({}, state, {
            showGraphs: !state.showGraphs,
        });
    }
    case SET_SHOW_CUSTOMER_CONTACTS: {
        return Object.assign({}, state, {
            showCustomerContacts: !state.showCustomerContacts,
        });
    }
    case SET_SHOW_IMPRESSIONS: {
        return Object.assign({}, state, {
            showImpressions: !state.showImpressions,
        });
    }
    case SET_SHOW_PER_HID: {
        return Object.assign({}, state, {
            showPerHid: !state.showPerHid,
        });
    }
    case SET_SEARCH_ACTIVE: {
        return Object.assign({}, state, {
            searchActive: action.value,
        });
    }
    case SET_USE_ORDER_PERIOD: {
        return Object.assign({}, state, {
            useOrderPeriod: !state.useOrderPeriod,
        });
    }
    case SET_RESULT_PERIOD: {
        return Object.assign({}, state, {
            resultPeriodFrom: action.value.from,
            resultPeriodTo: action.value.to,
        });
    }
    case SET_RESULT_SEARCH_TYPE: {
        return Object.assign({}, state, {
            resultSearchType: action.value,
        });
    }
    case SET_NOT_GROUPED_PRODUCTS: {
        return Object.assign({}, state, {
            notGroupedProducts: action.value,
        });
    }
    case LOCATION_CHANGE: {
        const { pathname, state: actionPayloadState } = action.payload.location || {};
        const isCompanyReportLocation = pathname === routeConstants.COMPANY_REPORT;

        if (isCompanyReportLocation) {
            const { companyreport } = actionPayloadState || {};
            const {
                formState,
                totalLeadCount,
                totalImpressionCount,
                totalDetailImpressionCount,
                totalListImpressionCount,
                productSummary,
                reportGraphData,
                companyCount,
                companyTableData,
                selectCompanyTableData,
            } = companyreport || initialState;

            return Object.assign({}, state, {
                totalLeadCount,
                totalImpressionCount,
                totalDetailImpressionCount,
                totalListImpressionCount,
                productSummary,
                location: action.payload.location,
                reportGraphData,
                formState,
                companyCount,
                companyTableData,
                selectCompanyTableData,
            });
        }
        return state;
    }
    default:
        return state;
    }
};

// ACTION CREATORS
const clearData = () => ({
    type: CLEAR_COMPANY_REPORT_DATA,
});
const clearForm = () => ({
    type: CLEAR_COMPANY_REPORT_FORM,
});

const internalSetData = stats => ({
    type: SET_COMPANY_REPORT_DATA,
    value: stats,
});

const internalSetFormState = formData => ({
    type: SET_COMPANY_REPORT_FORM_STATE,
    value: formData,
});

const internalSetCompanyReportLocation = location => ({
    type: SET_COMPANY_REPORT_LOCATION,
    value: location,
});

const setFormField = field => ({
    type: SET_COMPANY_REPORT_FORM_FIELD,
    value: field,
});

const setReportHeader = header => ({
    type: SET_COMPANY_REPORT_HEADER,
    value: header,
});

const setSearchActive = active => ({
    type: SET_SEARCH_ACTIVE,
    value: active,
});

const internalSetResultPeriod = period => ({
    type: SET_RESULT_PERIOD,
    value: period,
});

const internalSetResultSearchType = type => ({
    type: SET_RESULT_SEARCH_TYPE,
    value: type,
});

const setNotGroupedProducts = products => ({
    type: SET_NOT_GROUPED_PRODUCTS,
    value: products,
});

const setShowGraphs = () => ({
    type: SET_SHOW_GRAPHS,
});

const setShowCustomerContacts = () => ({
    type: SET_SHOW_CUSTOMER_CONTACTS,
});

const setUseOrderPeriod = () => ({
    type: SET_USE_ORDER_PERIOD,
});

const setShowImpressions = () => ({
    type: SET_SHOW_IMPRESSIONS,
});

const setShowPerHid = () => ({
    type: SET_SHOW_PER_HID,
});

// THUNK
const clearAll = () => (dispatch, getState) => {
    dispatch(clearData());
    dispatch(clearForm());
    const state = getState();
    const { formState } = state.companyreport;
    dispatch(push({
        pathname: routeConstants.COMPANY_REPORT,
        search: stringify(formState),
        state,
    }));
};

const getCompanyReportStats = (formData, orderSearch) => (dispatch, getState) => {
    dispatch(setLoading(true));
    dispatch(clearData());
    return companyStatsService.getCompanyReportStats(
        formData,
        orderSearch && getState().companyreport.useOrderPeriod,
    )
        .then((data) => {
            const {
                request: { from, to },
                response: {
                    graphDataPoint,
                    graphDate,
                    totalLeadCount,
                    totalImpressionCount,
                    totalDetailImpressionCount,
                    totalListImpressionCount,
                    companySummary,
                    productSummary,
                },
            } = data;

            const language = translation(getState().user.userPreferedLanguage);

            const reportGraphData = graphDataPoint && graphDate && formatGraphData(
                graphDataPoint,
                graphDate,
                language,
            );

            const companyTableData = companySummary && mapCompanySummaryToCompanyTable(
                companySummary,
                language,
            );

            const selectCompanyTableData = companySummary && mapCompanySummaryToSelectTable(
                companySummary,
                language,
            );

            dispatch(internalSetData({
                totalLeadCount,
                totalImpressionCount,
                totalDetailImpressionCount,
                totalListImpressionCount,
                companySummary,
                productSummary,
                reportGraphData,
                companyTableData,
                selectCompanyTableData,
            }));
            dispatch(internalSetResultSearchType(formData.searchType));
            dispatch(internalSetResultPeriod({ from, to }));
            dispatch(push({
                pathname: routeConstants.COMPANY_REPORT,
                search: stringify(formData),
                state: getState(),
            }));
            dispatch(internalSetCompanyReportLocation(getLocation(getState())));
            dispatch(setSearchActive(true));
            dispatch(setLoading(false));
            return true;
        })
        .catch((err) => {
            dispatch(setLoading(false));
            if (isUnauthorized(err.response)) {
                dispatch(logout());
            }
            dispatch(setSearchActive(true));
            dispatch(setErrorMessage(err.message));
        });
};

const getCompanyReportStatsForOrder = formData => (dispatch, getState) => {
    dispatch(setLoading(true));
    dispatch(clearData());
    dispatch(internalSetFormState(formData));
    return orderMetaService.getOrderMetaData(formData.ORDER_NUMBER)
        .then((data) => {
            const [order] = data;
            const { start, end, hid } = order || {};
            if (start && end && hid) {
                const newFormState = {
                    ...formData,
                    orderFrom: start,
                    orderTo: end > getYesterday() ? getYesterday() : end,
                    HID: hid.join(','),
                };
                dispatch(internalSetFormState(newFormState));
                return dispatch(getCompanyReportStats(newFormState, true));
            }
            dispatch(setLoading(false));
            dispatch(push({
                pathname: routeConstants.COMPANY_REPORT,
                search: stringify(formData),
                state: getState(),
            }));
            dispatch(setSearchActive(true));
            return false;
        })
        .catch((err) => {
            dispatch(setLoading(false));
            if (isUnauthorized(err.response)) {
                dispatch(logout());
            }
            dispatch(setSearchActive(true));
            dispatch(setErrorMessage(err.message));
        });
};

const searchOrderStatistics = orderId => (dispatch, getState) => {
    const newFormState = {
        ...getState().companyreport.formState,
        searchType: 'ORDER_NUMBER',
        ORDER_NUMBER: orderId,
    };
    dispatch(getCompanyReportStatsForOrder(newFormState));
};

const setInitialCompanyReportFormState = location => (dispatch, getState) => {
    const { formState } = getState().companyreport;
    const formParams = parse(location.search);

    const newFormState = {
        searchType: formParams.searchType || formState.searchType,
        HID: formParams.HID || formState.HID,
        ORG_NUMBER: formParams.ORG_NUMBER || formState.ORG_NUMBER,
        COMPANY_NAME: formParams.COMPANY_NAME || formState.COMPANY_NAME,
        ORDER_NUMBER: formParams.ORDER_NUMBER || formState.ORDER_NUMBER,
        to: formParams.to || formState.to,
        from: formParams.from || formState.from,
    };

    dispatch(internalSetFormState(newFormState));

    dispatch(replace({
        pathname: routeConstants.COMPANY_REPORT,
        search: stringify(newFormState),
        state: getState(),
    }));

    if (newFormState.searchType === 'ORDER_NUMBER' && newFormState.ORDER_NUMBER !== '') {
        return dispatch(getCompanyReportStatsForOrder(newFormState));
    }
    if (!!newFormState.HID || !!newFormState.ORG_NUMBER || !!newFormState.COMPANY_NAME) {
        return dispatch(getCompanyReportStats(newFormState));
    }
    return null;
};

export {
    initialState,
    getCompanyReportStats,
    getCompanyReportStatsForOrder,
    setInitialCompanyReportFormState,
    clearData,
    clearForm,
    clearAll,
    setFormField,
    setReportHeader,
    setShowGraphs,
    setShowCustomerContacts,
    setUseOrderPeriod,
    setShowImpressions,
    setShowPerHid,
    setNotGroupedProducts,
    searchOrderStatistics,
};

export default reducer;
